import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { fetchCustomers } from 'actions/action-filters';
import { fetchCustomerClinics, fetchSmsTemplatesForCustomer } from 'actions/action-lookups';
import { Controller, useForm } from 'react-hook-form';
import { nameOfFactory } from 'utils/types-util';
import { ReactSelect } from 'components/form/field/react_select';
import { useTypedSelector } from 'hooks/use-typed-selector';
import { NumberOption } from 'interfaces/forms/types';

interface ICustomerDropdownProps {
  selectedCustomerId: number;
  setSelectedCustomerId: (customerId: any) => void;
  isLabelOn?: boolean;
  isSelectedOption?: boolean;
  isMulti?: boolean;
  isDisabled?: boolean;
}

interface IFormFields {
  customerId: {
    label: string;
    value: number;
  };
}

const getFormFields = nameOfFactory<IFormFields>();

export const CustomerDropdown: React.FC<ICustomerDropdownProps> = (
  props: ICustomerDropdownProps,
): JSX.Element => {
  const dispatch = useDispatch();
  const customers = useTypedSelector(state => state.filters.customers);
  const selectedCustomerId = useTypedSelector(state => state.filters.selectedCustomerId);

  const form = useForm<any>();

  const useIsMulti = props.isMulti || false;
  const useIsDisabled = props.isDisabled || false;

  useEffect(() => {
    dispatch(fetchCustomers());
    dispatch(fetchCustomerClinics());
    dispatch(fetchSmsTemplatesForCustomer(selectedCustomerId));
  }, [fetchCustomers, fetchCustomerClinics]);

  const allCustomers = customers.map<NumberOption>(x => ({
    label: x.name,
    value: x.id,
  }));

  if (props.isSelectedOption) {
    allCustomers.push({ label: 'Select Customer', value: 0 });
  }

  const calcValue = (val: any) => {
    const defaultAll = [{ label: 'All', value: -1 }];

    if (!Array.isArray(val) && !useIsMulti) {
      return [val];
    }
    if (!Array.isArray(val) && useIsMulti) {
      return defaultAll;
    }
    if (!useIsDisabled) {
      const filteredVal = val.filter((v: { label: string; value: number }) => v.value !== -1);

      if (useIsMulti && val.length === 0) {
        return defaultAll;
      }
      return filteredVal;
    }
    return null;
  };

  const calcLabel = () => {
    if (props.isLabelOn) {
      return useIsMulti ? 'Customers' : 'Customer';
    }
    return undefined;
  };

  return (
    <Controller
      defaultValue={
        allCustomers.find(customer => customer.value === props.selectedCustomerId) ||
        allCustomers[0]
      }
      control={form.control}
      name={getFormFields('customerId')}
      render={(ctrlProps: any) => {
        return (
          <ReactSelect
            value={calcValue(ctrlProps?.field?.value)}
            fields={allCustomers}
            isMulti={useIsMulti}
            isDisabled={useIsDisabled}
            label={calcLabel()}
            handleOnChange={(value: any) => {
              const filteredVal = useIsMulti
                ? value
                    .filter((v: { label: string; value: number }) => v.value !== -1)
                    .map((v: { label: string; value: number }) => v.value)
                : value.value;

              form.setValue(getFormFields('customerId'), value);
              props.setSelectedCustomerId(filteredVal);
            }}
          />
        );
      }}
    />
  );
};

export default CustomerDropdown;
